import { useMemo } from 'react';
import { Container, Header, SpaceBetween } from '@cloudscape-design/components';
import { PlaceBasic, PlaceSourceSnapshot } from '../common/types';
import { SourceRatingRow } from './SourceRatingRow';
import {
  scorecardSourceIcon,
  scorecardSourceName,
  scorecardSourceSortIndex,
} from '../common/scorecardSources';

export type SourceRatingsSummaryProps = {
  place?: PlaceBasic;
  items?: SourceRatingsSummaryItem[];
  snapshotMap?: Map<string, PlaceSourceSnapshot>;
  showDealerRater?: boolean;
  showOpenTable?: boolean;
  showTripAdvisor?: boolean;
  showCarsCom?: boolean;
  showCarFax?: boolean;
  showEdmunds?: boolean;
  showCarGurus?: boolean;
  showBooking?: boolean;
  showExpedia?: boolean;
  showGolfPass?: boolean;
  onSourceChange?: (source: string) => void;
};

type SourceRatingItem = {
  id: string;
  name: string;
  icon: React.ReactNode;
  rating?: number;
  reviewCount?: number;
  link?: string;
};

export type SourceRatingsSummaryItem = {
  source: string;
  rating?: number | null;
  reviewCount?: number | null;
  link?: string | null;
};

function buildSourcesFromPlace(
  place: PlaceBasic,
  showDealerRater: boolean,
  showCarsCom: boolean,
  showCarFax: boolean,
  showEdmunds: boolean,
  showCarGurus: boolean,
  showOpenTable: boolean,
  showTripAdvisor: boolean,
  showBooking: boolean,
  showExpedia: boolean,
  showGolfPass: boolean,
): SourceRatingItem[] {
  const sources: SourceRatingItem[] = [
    {
      id: 'google',
      name: 'Google',
      icon: scorecardSourceIcon('google'),
      rating: place.rating ?? undefined,
      reviewCount: place.total_review_count ?? undefined,
      link: place.cid_url ?? undefined,
    },
    {
      id: 'yelp',
      name: 'Yelp',
      icon: scorecardSourceIcon('yelp'),
      rating: place.yelp_rating ?? undefined,
      reviewCount: place.yelp_total_review_count ?? undefined,
      link: place.yelp_link ?? undefined,
    },
    {
      id: 'bbb',
      name: 'BBB',
      icon: scorecardSourceIcon('bbb'),
      rating: place.bbb_rating ?? undefined,
      reviewCount: place.bbb_total_review_count ?? undefined,
      link: place.bbb_link ?? undefined,
    },
  ];
  if (showDealerRater)
    sources.push({
      id: 'dealerrater',
      name: 'DealerRater',
      icon: scorecardSourceIcon('dealerrater'),
      rating: place.dealerrater_rating ?? undefined,
      reviewCount: place.dealerrater_total_review_count ?? undefined,
      link: place.dealerrater_link ?? undefined,
    });
  if (showCarsCom)
    sources.push({
      id: 'carscom',
      name: 'Cars.com',
      icon: scorecardSourceIcon('carscom'),
      rating: place.carscom_rating ?? undefined,
      reviewCount: place.carscom_total_review_count ?? undefined,
      link: place.carscom_link ?? undefined,
    });
  if (showCarFax)
    sources.push({
      id: 'carfax',
      name: 'CarFax',
      icon: scorecardSourceIcon('carfax'),
      rating: place.carfax_rating ?? undefined,
      reviewCount: place.carfax_total_review_count ?? undefined,
      link: place.carfax_link ?? undefined,
    });
  if (showEdmunds)
    sources.push({
      id: 'edmunds',
      name: 'Edmunds',
      icon: scorecardSourceIcon('edmunds'),
      rating: place.edmunds_rating ?? undefined,
      reviewCount: place.edmunds_total_review_count ?? undefined,
      link: place.edmunds_link ?? undefined,
    });
  if (showCarGurus)
    sources.push({
      id: 'cargurus',
      name: 'CarGurus',
      icon: scorecardSourceIcon('cargurus'),
      rating: place.cargurus_rating ?? undefined,
      reviewCount: place.cargurus_total_review_count ?? undefined,
      link: place.cargurus_link ?? undefined,
    });
  if (showOpenTable)
    sources.push({
      id: 'opentable',
      name: 'OpenTable',
      icon: scorecardSourceIcon('opentable'),
      rating: place.opentable_rating ?? undefined,
      reviewCount: place.opentable_total_review_count ?? undefined,
      link: place.opentable_link ?? undefined,
    });
  if (showTripAdvisor)
    sources.push({
      id: 'tripadvisor',
      name: 'TripAdvisor',
      icon: scorecardSourceIcon('tripadvisor'),
      rating: place.tripadvisor_rating ?? undefined,
      reviewCount: place.tripadvisor_total_review_count ?? undefined,
      link: place.tripadvisor_link ?? undefined,
    });
  if (showBooking)
    sources.push({
      id: 'booking',
      name: 'Booking.com',
      icon: scorecardSourceIcon('booking'),
      rating: place.booking_rating ?? undefined,
      reviewCount: place.booking_total_review_count ?? undefined,
      link: place.booking_link ?? undefined,
    });
  if (showExpedia)
    sources.push({
      id: 'expedia',
      name: 'Expedia',
      icon: scorecardSourceIcon('expedia'),
      rating: place.expedia_rating ?? undefined,
      reviewCount: place.expedia_total_review_count ?? undefined,
      link: place.expedia_link ?? undefined,
    });
  if (showGolfPass)
    sources.push({
      id: 'golfpass',
      name: 'GolfPass',
      icon: scorecardSourceIcon('golfpass'),
      rating: place.golfpass_rating ?? undefined,
      reviewCount: place.golfpass_total_review_count ?? undefined,
      link: place.golfpass_link ?? undefined,
    });
  return sources;
}

export function SourceRatingsSummary({
  place,
  items,
  snapshotMap,
  showDealerRater = false,
  showOpenTable = false,
  showTripAdvisor = false,
  showCarsCom = false,
  showCarFax = false,
  showEdmunds = false,
  showCarGurus = false,
  showBooking = false,
  showExpedia = false,
  showGolfPass = false,
  onSourceChange,
}: SourceRatingsSummaryProps) {
  const sources: SourceRatingItem[] = useMemo(() => {
    if (items) {
      return items.map((item) => ({
        id: item.source,
        name: scorecardSourceName(item.source),
        icon: scorecardSourceIcon(item.source),
        rating: item.rating ?? undefined,
        reviewCount: item.reviewCount ?? undefined,
        link: item.link ?? undefined,
      }));
    }
    if (snapshotMap && snapshotMap.size > 0) {
      return [...snapshotMap.values()]
        .filter((s) => s.review_count != null)
        .map((s) => ({
          id: s.source,
          name: scorecardSourceName(s.source),
          icon: scorecardSourceIcon(s.source),
          rating: s.rating ?? 0,
          reviewCount: s.review_count ?? 0,
          link: s.link ?? undefined,
        }));
    }
    if (!place) return [];
    // Fallback: build from flat Place fields (old reports without snapshots)
    return buildSourcesFromPlace(
      place,
      showDealerRater,
      showCarsCom,
      showCarFax,
      showEdmunds,
      showCarGurus,
      showOpenTable,
      showTripAdvisor,
      showBooking,
      showExpedia,
      showGolfPass,
    );
  }, [
    items,
    snapshotMap,
    place,
    showDealerRater,
    showCarsCom,
    showCarFax,
    showEdmunds,
    showCarGurus,
    showOpenTable,
    showTripAdvisor,
    showBooking,
    showExpedia,
    showGolfPass,
  ]);

  // Show only sources that have collected data (rating or reviewCount present)
  const availableSources = sources
    .filter((source) => source.rating != null || source.reviewCount != null)
    .sort((a, b) => scorecardSourceSortIndex(a.id) - scorecardSourceSortIndex(b.id));

  if (availableSources.length === 0) {
    return null;
  }

  return (
    <div className="w-full min-w-0">
      <Container header={<Header variant="h3">Lifetime Stats</Header>}>
        <div className="flex flex-col gap-1">
          <SpaceBetween direction="vertical" size="xxs">
            {availableSources.map((source) => (
              <SourceRatingRow
                key={source.name}
                icon={source.icon}
                name={source.name}
                rating={source.rating}
                reviewCount={source.reviewCount}
                link={source.link}
                onClick={() => onSourceChange?.(source.id)}
              />
            ))}
          </SpaceBetween>
        </div>
      </Container>
    </div>
  );
}
